import { ModelNodeDataForm, Point3D, SpoolImportDto } from 'providers/api';
import { NodeRow } from '../types';
import { PCNode } from '../../MineModel/Nodes';
import PCPipe from '../../MineModel/Pipes/PCPipe';
import PCSpool from '../../MineModel/Spools/PCSpool';
import { getDefaultFlowFlags } from '../../MineModel/Spools';
import { Model } from '../../hooks/useModelState';
import Vector from '../../MineModel/Nodes/Vector';

// An edge, described by its endpoints. The pipe and the flow-loop length belong to the edge, never to
// either endpoint node.
export interface SpoolEdge {
  nodeAId: string;
  nodeBId: string;
  pipeId?: string;
  chokeLength?: number;
}

// A spool minted in the browser as a preview is drawn green until the model reloads from the server.
export const PREVIEW_SPOOL_COLOUR = 'green';

export interface CreateSpoolOptions {
  pipeId?: string;
  chokeLength?: number;
  spoolColour?: string;
}

interface CreateSpoolsOptions extends CreateSpoolOptions {
  nodeIndex?: Map<string, PCNode>;
}

interface CreateNodeAndSpoolResult {
  newNode: PCNode;
  spools: PCSpool[];
}

// A spool id is an opaque GUID minted wherever the edge is created — never derived from the endpoint
// ids. Node ids are renamed by every import and may legitimately contain any character, so a derived
// id both churns on rename and mis-parses. Matches the server's compact-GUID convention.
export const newSpoolId = (): string => {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes).map((byte) => byte.toString(16).padStart(2, '0')).join('');
};

const findPipe = (model: Model, pipeId: string | undefined): PCPipe | undefined => (pipeId
  ? Object.values(model.pipes).find((pipe) => pipe.pipeId === pipeId || pipe.name === pipeId)
  : undefined);

// Flow flags are derived from geometry only at the moment the spool is minted. Once it exists the
// spool's own flags are the source of truth and are never re-derived.
export const createSpool = (
  nodeA: PCNode,
  nodeB: PCNode,
  model: Model,
  options: CreateSpoolOptions = {},
): PCSpool => {
  if (nodeA.id === nodeB.id) {
    throw new Error(`A spool cannot start and end at the same node (${nodeA.id})`);
  }

  const pipe = findPipe(model, options.pipeId) ?? findPipe(model, nodeA.pipeId) ?? findPipe(model, nodeB.pipeId);
  if (!pipe) {
    throw new Error(`Pipe ${options.pipeId ?? nodeA.pipeId ?? '(undefined)'} not found`);
  }

  const { allowAB, allowBA } = getDefaultFlowFlags(nodeA, nodeB, model.project.biDirectionalBoreholes);

  return new PCSpool(
    newSpoolId(),
    options.chokeLength ?? 0,
    nodeA,
    nodeB,
    pipe,
    allowAB,
    allowBA,
    options.spoolColour,
  );
};

export const createSpools = (
  edges: SpoolEdge[],
  model: Model,
  options: CreateSpoolsOptions = {},
): PCSpool[] => {
  const lookup = options.nodeIndex ?? new Map<string, PCNode>(Object.values(model.nodes).map((node) => [node.id, node]));

  return edges.reduce<PCSpool[]>((acc, edge) => {
    const nodeA = lookup.get(edge.nodeAId);
    const nodeB = lookup.get(edge.nodeBId);
    if (!nodeA || !nodeB || nodeA.id === nodeB.id) return acc;

    return [...acc, createSpool(nodeA, nodeB, model, {
      pipeId: edge.pipeId ?? options.pipeId,
      chokeLength: edge.chokeLength ?? options.chokeLength,
      spoolColour: options.spoolColour,
    })];
  }, []);
};

// The row's previous nodes plus its optional terminating node describe the edges the new node joins.
// The flow-loop length the row carries goes onto the first incoming edge — a loop is a length of pipe,
// so it belongs to a spool, not to a node.
export const edgesForNodeRow = (nodeData: NodeRow): SpoolEdge[] => {
  const previousIds = (nodeData.previousNodeId ?? []).filter((id) => !!id && id !== nodeData.nodeId);
  const terminatingIds = nodeData.endAtExistingNode && nodeData.endAtExistingNode !== nodeData.nodeId
    ? [nodeData.endAtExistingNode]
    : [];
  const loopLength = Number(nodeData.frictionLoopLength ?? 0) || 0;

  return [...previousIds, ...terminatingIds].map((otherId, index) => ({
    nodeAId: otherId,
    nodeBId: nodeData.nodeId,
    pipeId: nodeData.pipeType,
    chokeLength: index === 0 ? loopLength : 0,
  }));
};

export const createNodeFromRow = (nodeData: NodeRow, isPreview?: boolean): PCNode => new PCNode({
  coords: new Vector(Number(nodeData.x), Number(nodeData.y), Number(nodeData.z)),
  id: nodeData.nodeId,
  pipeId: nodeData.pipeType,
  area: nodeData.area,
  level: nodeData.level,
  pit: nodeData.pitName,
  isPit: !!nodeData.pitName,
  isManuallyAdded: isPreview !== false,
});

export const createNodeAndSpool = (
  nodeData: NodeRow,
  model: Model,
  isPreview?: boolean,
  spoolColour?: string,
): CreateNodeAndSpoolResult => {
  const newNode = createNodeFromRow(nodeData, isPreview);

  const nodeIndex = new Map<string, PCNode>(Object.values(model.nodes).map((node) => [node.id, node]));
  nodeIndex.set(newNode.id, newNode);

  const spools = createSpools(edgesForNodeRow(nodeData), model, { nodeIndex, spoolColour });

  return { newNode, spools };
};

export const toSpoolImportDto = (spool: PCSpool): SpoolImportDto => ({
  SPOOL_ID: spool.spoolId,
  nodeAId: spool.pointA.id,
  nodeBId: spool.pointB.id,
  pipeId: spool.pipe.pipeId,
  allowFlowAB: spool.allowFlowAB,
  allowFlowBA: spool.allowFlowBA,
  chokeLength: spool.chokeLength,
});

export const createNodeFormData = (nodeData: NodeRow): ModelNodeDataForm => {
  const point: Point3D = {
    x: Number(nodeData.x),
    y: Number(nodeData.y),
    z: Number(nodeData.z),
  };

  return {
    nodeId: nodeData.nodeId,
    point,
    pipeId: nodeData.pipeType,
    pit: nodeData.pitName,
    isStope: false,
    isPit: Boolean(nodeData.pitName),
    stopeData: undefined,
    level: nodeData.level,
    area: nodeData.area,
    isPlantNode: false,
    ruptureDiskPressure: 0,
    chokeHead: 0,
  };
};
